import { useRef } from 'react';
import { Table, Spinner, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchClientPortalDeliveries, CLIENT_PORTAL_PAGE_SIZE } from '../api/clientPortalApi';
import { ClientPortalLayout } from '../components/ClientPortalLayout';
import { TablePageLayout } from '../components/TablePageLayout';
import { formatCurrency, formatDate, formatMoveState } from '../utils/formatters';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

function TrackingCell({ trackingRef, trackingUrl }) {
  if (!trackingUrl) {
    return trackingRef || '-';
  }

  return (
    <a href={trackingUrl} target="_blank" rel="noreferrer">
      {trackingRef || 'Tracking link'}
    </a>
  );
}

function getDeliveryTotals(delivery) {
  return delivery.moves.reduce((totals, move) => ({
    totalPrice: totals.totalPrice + move.so_line_total_price.amount,
    ordered: totals.ordered + move.product_uom_qty,
    ready: totals.ready + move.quantity,
    backorder: totals.backorder + Math.max(0, move.product_uom_qty - move.quantity),
    currency: move.so_line_total_price.currency || totals.currency,
  }), {
    totalPrice: 0,
    ordered: 0,
    ready: 0,
    backorder: 0,
    currency: 'EUR',
  });
}

export function ClientPortalDeliveriesPage() {
  const loadMoreRef = useRef(null);

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetched,
  } = useInfiniteQuery({
    queryKey: ['client-portal-deliveries'],
    queryFn: fetchClientPortalDeliveries,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentCount = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return lastPage.results.length === CLIENT_PORTAL_PAGE_SIZE ? currentCount : undefined;
    },
  });

  const deliveries = data?.pages.flatMap((page) => page.results) || [];

  const { containerRef, handleScroll } = useScrollRestoration(
    'clientPortalDeliveriesScroll',
    isFetched
  );

  useInfiniteScroll({
    loadMoreRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootRef: containerRef,
    dependencyArray: [deliveries.length]
  });

  return (
    <ClientPortalLayout>
      <TablePageLayout
        title="All deliveries"
        description="View all your deliveries."
        isLoading={isLoading && deliveries.length === 0}
        isError={isError}
        error={error}
        containerRef={containerRef}
        onScroll={handleScroll}
      >
        <Table responsive striped hover className="mb-0">
          <thead>
            <tr>
              <th>Transfer</th>
              <th>SO</th>
              <th>Company</th>
              <th>Status</th>
              <th>Tracking</th>
              <th>ETA</th>
              <th>Total price</th>
              <th>Total qty</th>
              <th>Reserved</th>
              <th>On backorder</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.length === 0 && !isLoading ? (
              <tr>
                <td colSpan="10" className="text-center py-4 text-muted">
                  No deliveries found.
                </td>
              </tr>
            ) : (
              deliveries.map((delivery) => {
                const totals = getDeliveryTotals(delivery);
                const trackingRef = delivery.carrier_tracking_ref === 'False' ? null : delivery.carrier_tracking_ref;
                const trackingUrl = delivery.carrier_tracking_url_manual === 'False' ? null : delivery.carrier_tracking_url_manual;

                return (
                  <tr key={delivery.name}>
                    <td>
                      <Link to={`/client/deliveries/${delivery.name}`}>{delivery.name}</Link>
                    </td>
                    <td>
                      {delivery.sales_order ? (
                        <Link to={`/client/orders/${delivery.sales_order.name}`}>
                          {delivery.sales_order.name}
                        </Link>
                      ) : '-'}
                    </td>
                    <td>{delivery.partner?.name || '-'}</td>
                    <td>{formatMoveState(delivery.state) || '-'}</td>
                    <td><TrackingCell trackingRef={trackingRef} trackingUrl={trackingUrl} /></td>
                    <td>{formatDate(delivery.scheduled_date)}</td>
                    <td>{formatCurrency({ amount: totals.totalPrice, currency: totals.currency })}</td>
                    <td>{totals.ordered}</td>
                    <td>{totals.ready}</td>
                    <td>{totals.backorder}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>

        {(hasNextPage || isFetchingNextPage) && (
          <div ref={loadMoreRef} className="text-center py-4 border-top">
            {isFetchingNextPage ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Loading more...
              </>
            ) : (
              <Button variant="link" onClick={() => fetchNextPage()}>
                Load more
              </Button>
            )}
          </div>
        )}
      </TablePageLayout>
    </ClientPortalLayout>
  );
}
